/**
 * Pantalla de acceso.
 *
 * Dos variantes según el modo de autenticación (`auth/keycloak.ts`):
 *
 * - **Keycloak**: la persona ya ha entrado en el login corporativo. Aquí solo se
 *   pide su perfil a DOX y, si tiene varias empresas, se elige una; con una sola
 *   se entra directamente. Sin permisos en DOX se explica y se ofrece salir.
 * - **Local** (desarrollo): empresa, usuario y contraseña.
 *
 * En los dos casos se lleva a cada persona a su sitio según sus permisos en la
 * empresa elegida: un operario de salidas a la pantalla de emisión; quien
 * administra, al back-office (apdo. 12.1).
 *
 * Solo el formulario: el manual está dentro de la aplicación, en su propio
 * menú, una vez identificado.
 */

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { api, ErrorPeticion, type Credenciales, type Perfil } from '../api/cliente'
import type { Empresa } from '../api/tipos'
import { cerrarSesionCorporativa, conKeycloak } from '../auth/keycloak'
import { useSesion } from '../contexto/Sesion'
import { Cargando } from './comunes'
import { AlternarTema } from './AlternarTema'
import { LogoEsGroup } from './LogoEsGroup'
import { SelectorIdioma } from './SelectorIdioma'

/** A dónde se lleva a un usuario recién identificado, según sus permisos en la empresa. */
export function destinoInicial(perfil: Perfil, empresaId?: string): string | null {
  const permisos = (empresaId && perfil.permisos_por_empresa?.[empresaId]) || perfil.permisos
  const admin =
    perfil.es_administrador ||
    ['documentos', 'transportistas', 'direcciones', 'empresas', 'configuracion', 'usuarios'].some((p) =>
      permisos.includes(p),
    )
  if (admin) return '/admin'
  if (permisos.includes('salidas')) return '/salidas'
  return null
}

export function Acceso() {
  return conKeycloak ? <AccesoCorporativo /> : <AccesoLocal />
}

/** Cabecera común: marca, idioma y tema. */
function Marco({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
          <LogoEsGroup alto={38} conDivisor />
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {t('app.nombre')}
            </Typography>
            <Typography
              component="span"
              color="text.secondary"
              sx={{ fontSize: '0.85rem', ml: 1, minWidth: 0 }}
            >
              : {t('app.descripcion')}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <SelectorIdioma />
          <AlternarTema color="primary" />
        </Stack>
      </Stack>
      <Card>
        <CardContent sx={{ maxWidth: 460, mx: 'auto', width: '100%' }}>{children}</CardContent>
      </Card>
    </Container>
  )
}

/**
 * Entrada con la sesión corporativa ya abierta: perfil de DOX y empresa.
 */
function AccesoCorporativo() {
  const { t } = useTranslation()
  const { entrar } = useSesion()
  const navegar = useNavigate()

  const [perfil, setPerfil] = useState<Perfil | null>(null)
  const [empresas, setEmpresas] = useState<Pick<Empresa, 'empresa_id' | 'razon_social'>[]>([])
  const [empresaId, setEmpresaId] = useState('')
  const [error, setError] = useState('')

  const continuar = useCallback(
    (datos: Perfil, empresa: string) => {
      const destino = destinoInicial(datos, empresa)
      if (destino === null) {
        setError(t('acceso.sinPermisosCorporativo'))
        return
      }
      const credenciales: Credenciales = { usuario: datos.username, clave: '', empresaId: empresa }
      entrar(credenciales, datos)
      navegar(destino, { replace: true })
    },
    [entrar, navegar, t],
  )

  useEffect(() => {
    let vigente = true
    Promise.all([api.perfil(), api.empresasParaSeleccion().catch(() => [])])
      .then(([datos, todas]) => {
        if (!vigente) return
        const suyas = todas.filter((e) => datos.empresas.includes(e.empresa_id))
        setPerfil(datos)
        setEmpresas(suyas.length > 0 ? suyas : datos.empresas.map((id) => ({ empresa_id: id, razon_social: id })))
        // Una sola empresa: no hay nada que elegir.
        if (datos.empresas.length === 1) continuar(datos, datos.empresas[0])
        else setEmpresaId(datos.empresas[0] ?? '')
      })
      .catch((fallo) => {
        if (!vigente) return
        setError(
          fallo instanceof ErrorPeticion && fallo.estado === 403
            ? t('acceso.sinPermisosCorporativo')
            : t('acceso.errorPerfil'),
        )
      })
    return () => {
      vigente = false
    }
  }, [continuar, t])

  if (!perfil && !error) {
    return (
      <Marco>
        <Cargando />
      </Marco>
    )
  }

  return (
    <Marco>
      {perfil && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('acceso.conectadoComo', { usuario: perfil.nombre || perfil.username })}
        </Typography>
      )}
      <Stack spacing={2.5}>
        {perfil && empresas.length > 1 && (
          <FormControl fullWidth>
            <InputLabel id="etiqueta-empresa-corporativa">{t('acceso.elijaEmpresa')}</InputLabel>
            <Select
              labelId="etiqueta-empresa-corporativa"
              label={t('acceso.elijaEmpresa')}
              value={empresaId}
              onChange={(evento) => setEmpresaId(evento.target.value)}
            >
              {empresas.map((empresa) => (
                <MenuItem key={empresa.empresa_id} value={empresa.empresa_id}>
                  {empresa.razon_social}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {error && <Alert severity="warning">{error}</Alert>}
        {perfil && !error && empresas.length > 1 && (
          <Button
            variant="contained"
            size="large"
            disabled={!empresaId}
            onClick={() => continuar(perfil, empresaId)}
          >
            {t('acceso.entrar')}
          </Button>
        )}
        <Button onClick={cerrarSesionCorporativa}>{t('acceso.cambiarUsuario')}</Button>
      </Stack>
    </Marco>
  )
}

function AccesoLocal() {
  const { t } = useTranslation()
  const { entrar } = useSesion()
  const navegar = useNavigate()

  const [empresas, setEmpresas] = useState<Pick<Empresa, 'empresa_id' | 'razon_social'>[]>([])
  const [empresaId, setEmpresaId] = useState('')
  const [usuario, setUsuario] = useState('')
  const [clave, setClave] = useState('')
  const [error, setError] = useState('')
  const [errorEmpresas, setErrorEmpresas] = useState(false)
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    // Sin credenciales todavía: se usa el endpoint de selección, que no las pide.
    // Si el servicio no responde, se cae al campo de texto para que se pueda
    // entrar escribiendo el código de empresa.
    api
      .empresasParaSeleccion()
      .then((lista) => {
        setEmpresas(lista)
        if (lista.length > 0) setEmpresaId((actual) => actual || lista[0].empresa_id)
      })
      .catch(() => setErrorEmpresas(true))
  }, [])

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault()
    setError('')
    setEnviando(true)
    try {
      const perfil = await api.login(usuario.trim(), clave)
      const destino = destinoInicial(perfil, empresaId.trim())
      if (destino === null) {
        setError(t('acceso.sinPermisos'))
        return
      }
      if (perfil.empresas.length === 0) {
        setError(t('acceso.sinEmpresas'))
        return
      }
      // La empresa elegida debe ser una de las suyas. Acceder a una sociedad
      // ajena se rechaza (aislamiento entre las 6 compañías, apdo. 12.1); no se
      // cuela silenciosamente por otra empresa.
      if (!perfil.empresas.includes(empresaId.trim())) {
        setError(t('acceso.sinAccesoEmpresa'))
        return
      }
      const credenciales: Credenciales = { usuario: usuario.trim(), clave, empresaId: empresaId.trim() }
      entrar(credenciales, perfil)
      navegar(destino, { replace: true })
    } catch {
      setError(t('acceso.errorCredenciales'))
    } finally {
      setEnviando(false)
    }
  }

  const listo = empresaId.trim() !== '' && usuario.trim() !== '' && clave !== ''

  return (
    <Marco>
      <Box component="form" onSubmit={enviar}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('acceso.subtitulo')}
            </Typography>

            {errorEmpresas && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('acceso.errorEmpresas')}
              </Alert>
            )}

            <Stack spacing={2.5}>
              {empresas.length > 0 ? (
                <FormControl fullWidth>
                  <InputLabel id="etiqueta-empresa">{t('acceso.elijaEmpresa')}</InputLabel>
                  <Select
                    labelId="etiqueta-empresa"
                    label={t('acceso.elijaEmpresa')}
                    value={empresaId}
                    onChange={(evento) => setEmpresaId(evento.target.value)}
                  >
                    {empresas.map((empresa) => (
                      <MenuItem key={empresa.empresa_id} value={empresa.empresa_id}>
                        {empresa.razon_social}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  label={t('app.empresa')}
                  value={empresaId}
                  onChange={(evento) => setEmpresaId(evento.target.value.toUpperCase())}
                  fullWidth
                  required
                  slotProps={{ htmlInput: { maxLength: 10 } }}
                />
              )}

              <TextField
                label={t('acceso.campoUsuario')}
                value={usuario}
                onChange={(evento) => setUsuario(evento.target.value)}
                fullWidth
                required
                autoComplete="username"
                autoFocus
              />
              <TextField
                label={t('acceso.campoClave')}
                type="password"
                value={clave}
                onChange={(evento) => setClave(evento.target.value)}
                fullWidth
                required
                autoComplete="current-password"
              />

              {error && <Alert severity="error">{error}</Alert>}

              <Button type="submit" variant="contained" size="large" disabled={!listo || enviando}>
                {t('acceso.entrar')}
              </Button>
            </Stack>
      </Box>
    </Marco>
  )
}
